/** Active-feed DOM identity only. Re-resolve every ID from the current authorized
 * snapshot; removed/hidden/expired projections cannot survive reconciliation.
 * No route cache or independent post state is retained. */
import { createGameCards } from './34-game-cards.mjs';

export function createFeedReconciliation(ctx, caughtUpMarker) {
  const games = createGameCards(ctx);
  const cardState = new WeakMap();
  let mountedSurface = null;
  function surfaceKey() {
    return JSON.stringify([
      ctx.state?.me.id,
      ctx.experience,
      ctx.feedScope,
      ctx.mode,
      ctx.contentScope ?? 'ordinary',
      ctx.discoveryPolicyVersion,
      ctx.state.settings?.learning,
      ctx.state.settings?.interestEpoch,
    ]);
  }
  /** Reuse only this actor's active feed structure; all content still re-resolves
   * through the current authoritative snapshot before keyed reconciliation. */
  function canReuseFeedSurface() {
    return (
      ['feed', 'learning'].includes(ctx.route()[0]) &&
      (ctx.experience === 'wall' || ctx.state.features?.[`${ctx.experience}-feed`] !== false) &&
      (ctx.mode === 'latest' || ctx.state.features?.['adaptive-feed'] !== false) &&
      mountedSurface === surfaceKey() &&
      !!ctx.view.querySelector('#feed-list')
    );
  }
  function paintFeedItems() {
    const list = document.querySelector('#feed-list');
    if (!list) return;
    const existing = new Map(
      [...list.children]
        .filter((node) => node.dataset.postId)
        .map((node) => [node.dataset.postId, node]),
    );
    const posts = new Map(ctx.state.posts.map((post) => [post.id, post]));
    const desired = [];
    const existingGames = new Map(
      [...list.children]
        .filter((node) => node.dataset.gameSlot)
        .map((node) => [node.dataset.gameSlot, node]),
    );
    let ordinal = 0;
    let marked = false;
    const previousMarker = list.querySelector('.caught-up-marker');
    for (const item of ctx.feed.items) {
      const current = posts.get(item.id);
      if (
        !current ||
        ctx.state.settings.hidden.includes(item.id) ||
        ctx.state.settings.blocked?.includes(current.authorId) ||
        (current.expiresAt && current.expiresAt <= Date.now())
      )
        continue;
      if (item.seen && !marked) {
        desired.push(previousMarker ?? caughtUpMarker());
        marked = true;
      }
      // Signatures exist only on currently mounted nodes. New snapshots can reuse
      // identical content while changed bodies/ACL projections rebuild that card.
      const signature = JSON.stringify([current, item.reason, item.seen]);
      let card = existing.get(item.id);
      if (!card || cardState.get(card) !== signature) {
        card = ctx.postCard(current, {
          reason: item.reason,
          imagePriority:
            existing.size === 0 &&
            !desired.some((node) => node.dataset.postId) &&
            ctx.experience !== 'talk',
        });
        cardState.set(card, signature);
      }
      desired.push(card);
      const game = games.at(++ordinal);
      if (game) {
        const key = `${ordinal}:${game.id}`;
        const gameCard = existingGames.get(key) ?? games.render(game);
        gameCard.dataset.gameSlot = key;
        gameCard.refreshGame?.();
        desired.push(gameCard);
      }
    }
    if (ctx.feed.started && !ctx.feed.cursor && !marked)
      desired.push(previousMarker ?? caughtUpMarker());
    const retained = new Set(desired);
    for (const node of [...list.children])
      if (!retained.has(node)) {
        node.disposeGame?.();
        node.remove();
      }
    let position = list.firstChild;
    for (const node of desired) {
      if (node !== position) list.insertBefore(node, position);
      position = node.nextSibling;
    }
    ctx.observeReading();
    ctx.refreshMediaPlayback?.();
  }

  return {
    paint: paintFeedItems,
    dispose: games.dispose,
    canReuse: canReuseFeedSurface,
    mount: () => {
      mountedSurface = surfaceKey();
    },
    rememberCard: (card, item) =>
      cardState.set(card, JSON.stringify([item.post, item.reason, item.seen])),
  };
}
